import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import useStore from './sharedCode/stores/store';
import {
    Box,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useToast
} from '@chakra-ui/react';
import { Global } from '@emotion/react';
import { css } from '@chakra-ui/react';
import { ErrorBoundary } from 'react-error-boundary';
import DNDWrapper from './sharedCode/components/DndWrapper';
import TabsList from './platformFiles/components/TabsList';
import WsWrapper from './sharedCode/components/WsWrapper';
import { BootFailModal } from './sharedCode/components/ErrorAlertModal';
import WorkspacePicker from './sharedCode/components/WorkspacePicker';
import Categories from './sharedCode/components/Categories';
import Groups from './sharedCode/components/Groups';
import TopRightOptions from './sharedCode/components/TopRightOptions';
import Dock from './sharedCode/components/Dock';
import ModalProvider from './sharedCode/components/ModalProvider';
import MouseEventProivder from './sharedCode/components/MouseEventProvider';
import { initDashboard } from './platformFiles/webSpecific/initDashboard';
import { showToast } from './sharedCode/Utils/showToastWrapper';
import { denyTeam, joinTeam } from 'util/teamadmin';

// let didInit = false;

function DashboardSection(props) {
    const router = useRouter();
    const userStatus = useStore((state) => state.userStatus);
    const userData = useStore((state) => state.userData);
    const [teamInvite, setTeamInvite] = useState(null);

    const closeTeamModal = () => {
        setTeamInvite(null);
    };
    // useEffect(() => {
    //     if (!didInit) {
    //         didInit = true;
    //         useStore.getState().initApp();
    //     }
    // }, []);

    useEffect(() => {
        if (userStatus === 'loading') {
            initDashboard();
        }
    }, []);

    useEffect(() => {
        if (userData) {
            if (userData && userData.teamInvite) {
                setTeamInvite(userData.teamInvite);
            }
        }
    }, [userData]);

    useEffect(() => {
        if (router.query.paid) {
            showToast(
                'Your payment was successful!',
                'You are now subscribed to the Pro Plan. ',
                'success'
            );
            // showSuccessNotification(
            //     'Your payment was successful!',
            //     'You are now subscribed to the Pro Plan. '
            // );
        }
    }, [router]);

    return (
        <Box bg="gray.100" h="100vh" w="100vw">
            <Global
                styles={css`
                    .groupScrollArea::-webkit-scrollbar-thumb {
                        opacity: 0;
                        transition: 0.3s;
                    }

                    .groupScrollArea:hover::-webkit-scrollbar-thumb {
                        opacity: 1;
                        background-color: #a3a4a430;
                    }

                    .groupScrollArea::-webkit-scrollbar-thumn:hover {
                        background-color: #50565e;
                    }
                `}
            />
            <ErrorBoundary FallbackComponent={BootFailModal} onReset={() => {}}>
                {userStatus !== 'loading' && (
                    <>
                        <DNDWrapper>
                            <TabsList />
                            <WsWrapper>
                                <Flex
                                    data-testid="dashboard-header"
                                    p={{
                                        base: '16px 16px 0',
                                        md: '22px 24px 0'
                                    }}
                                    alignItems="center"
                                    flexWrap={{ base: 'wrap', md: 'nowrap' }}
                                >
                                    <Box order={1} flexShrink={0}>
                                        <WorkspacePicker />
                                    </Box>
                                    <Box
                                        order={{ base: 3, md: 2 }}
                                        flex={{
                                            base: '0 0 100%',
                                            md: '0 1 auto'
                                        }}
                                        minW={0}
                                        mt={{ base: '8px', md: 0 }}
                                    >
                                        <Categories />
                                    </Box>
                                    <TopRightOptions />
                                </Flex>
                                <Groups />
                            </WsWrapper>
                            <Dock />
                        </DNDWrapper>
                        <ModalProvider />
                        <MouseEventProivder />
                    </>
                )}
            </ErrorBoundary>
            {teamInvite && (
                <TeamInviteModal
                    teamInvite={teamInvite}
                    closeTeamModal={closeTeamModal}
                />
            )}
        </Box>
    );
}

export default DashboardSection;

const TeamInviteModal = ({ teamInvite, closeTeamModal }) => {
    const userData = useStore((state) => state.userData);
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);

    const joinTeamClick = async () => {
        if (!userData?.team) {
            toast({
                title: 'Error',
                description: 'Team information not found. Please refresh the page.',
                status: 'error',
                duration: 4400,
                isClosable: true
            });
            return;
        }

        setIsLoading(true);
        try {
            await joinTeam(
                userData.name || '',
                userData.userId,
                userData.email,
                userData.team
            );
            
            closeTeamModal();
            toast({
                title: "You're in the team!",
                description: 'Go to Settings → Team for a full overview of your team.',
                status: 'success',
                duration: 4400,
                isClosable: true
            });
            
            // Refresh to update user data
            window.location.reload();
        } catch (error) {
            setIsLoading(false);
            toast({
                title: 'Failed to join team',
                description: error?.message || 'Please contact support and we will help you',
                status: 'error',
                duration: 4400,
                isClosable: true
            });
        }
    };

    const denyInvite = async () => {
        if (!userData?.team) {
            closeTeamModal();
            return;
        }

        setIsLoading(true);
        try {
            await denyTeam(
                userData.userId,
                userData.email,
                userData.team
            );
            
            closeTeamModal();
            toast({
                title: 'Invite declined',
                description: 'You have declined the team invitation.',
                status: 'info',
                duration: 3000,
                isClosable: true
            });
            
            // Refresh to update user data
            window.location.reload();
        } catch (error) {
            setIsLoading(false);
            toast({
                title: 'Error declining invite',
                description: 'Please try again or contact support.',
                status: 'error',
                duration: 4400,
                isClosable: true
            });
        }
    };

    return (
        <Modal isOpen={true} onClose={closeTeamModal} isCentered size="lg">
            <ModalOverlay />
            <ModalContent rounded="16px">
                <ModalHeader
                    pt="2.375rem"
                    fontSize="20px"
                    //mt="-8px"
                    pb="8px"
                    display="flex"
                >
                    {/* <TeamIcon /> */}
                    Invititation to join team
                </ModalHeader>
                <ModalCloseButton
                    mt="4px"
                    size="sm"
                    rounded="6px"
                    opacity={0.6}
                />
                <ModalBody>
                    You have been invited by <b>{teamInvite?.owner}</b> to join
                    the team: <b>{teamInvite?.name}</b>. If you join you will be
                    upgraded to the team plan.
                </ModalBody>
                <ModalFooter mt="12px">
                    <Button
                        mr={3}
                        onClick={denyInvite}
                        rounded="8px"
                        colorScheme={'red'}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                    >
                        Deny invite
                    </Button>
                    <Button
                        rounded="8px"
                        color="#FFF"
                        backgroundImage="linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)"
                        _hover={{
                            backgroundImage:
                                'linear-gradient(145deg, #3592FE 0%, #2E7EDB 100%)',
                            boxShadow: '0 10px 16px -8px rgba(49, 144, 255,0.5)'
                        }}
                        onClick={joinTeamClick}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                    >
                        Join team
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
